import {
    GlobalNavigationDrawerAndAppBarOffset,
    useGlobalReservedAppBarHeight,
    useGlobalReservedNavDrawerWidth,
} from '@/components/GlobalNavigationDrawer';
import {
    NavigationDrawerAndAppBarOffset,
    useReservedAppBarHeight,
    useReservedNavDrawerWidth,
} from '@/components/NavigationDrawer/NavigationDrawer';
import { AxleErrorBoundary } from '@/fetching/errorBoundaries';
import { AxleTheme } from '@/theme';
import { FeatureFlag } from '@/types/featureFlag';
import { useIsDown } from '@/utils/mediaBreakpoints';
import { Children, Component } from '@/utils/reactTypes';
import { useFeatureFlagEnabled } from '@/utils/useFeatureFlagEnabled';
import { Box, Breakpoint } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
    type FC,
    type ForwardRefRenderFunction,
    Fragment,
    forwardRef,
    useEffect,
    useRef,
} from 'react';
import { Helmet } from 'react-helmet-async';
import SplitterLayout from 'react-splitter-layout';
import 'react-splitter-layout/lib/index.css';

type AxlePageBaseProps = {
    children: Children;
    noPadding: boolean;
};

const AxlePageBaseReffed: ForwardRefRenderFunction<HTMLDivElement, AxlePageBaseProps> = (
    props,
    ref,
) => {
    const { children, noPadding } = props;

    return (
        <Box p={noPadding ? 0 : 3} ref={ref}>
            {children}
        </Box>
    );
};

const AxlePageBase = forwardRef(AxlePageBaseReffed);

export const useSplitLayoutStyles = makeStyles<typeof AxleTheme>(() => ({
    container: {
        position: 'relative',
        '& .splitter-layout > .layout-splitter': {
            width: '8px',
            height: '100%', // transition: 'box-shadow 0.3s ease-in-out',
            '&:hover': {
                // position: 'relative',
                // left: '10px',
                // background: 'red',
                // zIndex: 1,
                // width: '8px',
                // marginLeft: '-3px',
                // borderLeft: '10px solid rgba(255, 255, 255, 0)',
                // borderRight: '10px solid rgba(255, 255, 255, 0)',
                // boxShadow: 'inset 0 0 0 5px #CCC',
            },
        },
        '& .splitter-layout.splitter-layout-vertical > .layout-splitter': {
            height: '8px',
            width: '100%',
        },
    },
}));

type MapSize = {
    mapBreakpoint: Breakpoint;
    mapHorizontalPercentageDefault: number;
    mapVerticalPercentageDefault: number;
    mapFirst: boolean;
};

type AxlePageWithMapProps = {
    children: Children;
    map: Component;
    mapSize: Partial<MapSize>;
    noPadding: boolean;
};

const AxlePageWithMapReffed: ForwardRefRenderFunction<HTMLDivElement, AxlePageWithMapProps> = (
    props,
    ref,
) => {
    const {
        children,
        map,
        mapSize: {
            mapBreakpoint = 'md',
            mapHorizontalPercentageDefault = 50,
            mapVerticalPercentageDefault = 25,
            mapFirst = false,
        } = {},
        noPadding,
    } = props;

    const hasGlobalNavigationV2 = useFeatureFlagEnabled(FeatureFlag.GLOBAL_NAVIGATION_V2);
    const refToSplitter = useRef<SplitterLayout>(null);
    const globalNavDrawerWidth = useGlobalReservedNavDrawerWidth();
    const legacyNavDrawerWidth = useReservedNavDrawerWidth();
    const globalAppBarHeight = useGlobalReservedAppBarHeight();
    const legacyAppBarHeight = useReservedAppBarHeight();
    const classes = useSplitLayoutStyles();
    const isVerticalLayout = useIsDown(mapBreakpoint);

    const navigationDrawerWidth = hasGlobalNavigationV2
        ? globalNavDrawerWidth
        : legacyNavDrawerWidth;
    const appBarHeight = hasGlobalNavigationV2 ? globalAppBarHeight : legacyAppBarHeight;
    const secondaryInitialSize = isVerticalLayout
        ? mapVerticalPercentageDefault
        : mapHorizontalPercentageDefault;
    const secondaryInitialSizeRef = useRef(secondaryInitialSize);

    useEffect(() => {
        secondaryInitialSizeRef.current = secondaryInitialSize;
    }, [secondaryInitialSize]);

    // When we change the layout from horizontal to vertical and vice versa, the splitter
    //  does not use the initial height/width for that layout. So we have to
    //  hack into the internals of the splitter component and do this ourselves.
    // Otherwise, if we had a 50% initial width in the horizontal layout, then
    //  switched, the splitter would use 50% of the vertical height instead of
    //  whatever we set as the default vertical height.
    useEffect(() => {
        const { current: splitter } = refToSplitter;

        if (!splitter?.container) {
            return;
        }

        const { current: secondaryPaneSize } = secondaryInitialSizeRef;
        const containerRect = splitter.container.getBoundingClientRect();
        const totalSize = isVerticalLayout ? containerRect.height : containerRect.width;
        const secondarySize = (secondaryPaneSize / 100) * totalSize;

        if (secondarySize !== Infinity && secondarySize !== 0) {
            splitter.setState({ secondaryPaneSize });
        }
    }, [isVerticalLayout]);

    return (
        <Box
            className={classes.container}
            sx={{
                width: `calc(100vw - ${navigationDrawerWidth})`,
                height: `calc(100vh - ${appBarHeight})`,
                transition: 'width 0.3s ease, height 0.3s ease',
            }}
        >
            <SplitterLayout
                ref={refToSplitter}
                vertical={isVerticalLayout}
                percentage
                primaryMinSize={30}
                secondaryMinSize={10}
                secondaryInitialSize={secondaryInitialSize}
                primaryIndex={mapFirst ? 1 : 0}
            >
                {mapFirst ? <AxleErrorBoundary>{map}</AxleErrorBoundary> : null}
                <AxlePageBase noPadding={noPadding} ref={ref}>
                    {children}
                </AxlePageBase>
                {!mapFirst ? <AxleErrorBoundary>{map}</AxleErrorBoundary> : null}
            </SplitterLayout>
        </Box>
    );
};

export const AxlePageWithMap = forwardRef(AxlePageWithMapReffed);

type PageTitleProps = {
    pageTitle?: string;
};

const PageTitle: FC<PageTitleProps> = (props) => {
    const { pageTitle } = props;

    if (typeof pageTitle !== 'string') {
        return null;
    }

    return (
        <Helmet>
            <title>{pageTitle} | Axle Health</title>
        </Helmet>
    );
};

type AxlePageProps = {
    children: Children;
    map?: Component | undefined;
    mapSize?: Partial<MapSize>;
    noPadding?: boolean;
    noNavDrawer?: boolean;
    pageTitle?: string;
};

const AxlePageReffed: ForwardRefRenderFunction<HTMLDivElement, AxlePageProps> = (props, ref) => {
    const {
        children,
        map = undefined,
        mapSize = {},
        noPadding = false,
        // If you don't have a nav drawer, but are still using <AxlePage/>, you need to set this to true,
        // or there will be space reserved in the left/top for the nav drawer.
        noNavDrawer = false,
        pageTitle,
    } = props;
    const hasGlobalNavigationV2 = useFeatureFlagEnabled(FeatureFlag.GLOBAL_NAVIGATION_V2);

    const NavWrapper = hasGlobalNavigationV2
        ? GlobalNavigationDrawerAndAppBarOffset
        : NavigationDrawerAndAppBarOffset;
    const Wrapper = noNavDrawer ? Fragment : NavWrapper;

    if (map) {
        return (
            <Wrapper>
                <AxlePageWithMap ref={ref} map={map} mapSize={mapSize} noPadding={noPadding}>
                    <PageTitle pageTitle={pageTitle} />
                    {children}
                </AxlePageWithMap>
            </Wrapper>
        );
    }

    return (
        <Wrapper>
            <AxlePageBase ref={ref} noPadding={noPadding}>
                <PageTitle pageTitle={pageTitle} />
                {children}
            </AxlePageBase>
        </Wrapper>
    );
};

export const AxlePage = forwardRef(AxlePageReffed);
